<script setup lang="ts">
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import { useDivisibleReceiver } from '@precon-tools-suite-by-shared/vue-divisibles';
import { computed, type Component } from 'vue';
import Button from 'primevue/button';
import Skeleton from 'primevue/skeleton';
import Sidebar from 'primevue/sidebar';

const props = withDefaults(
    defineProps<{
        position?: 'left' | 'right' | 'top' | 'bottom' | 'full';
        partial: Component | string;
        fallbackPartial?: Component | string | null;
        partialProps?: Record<string, unknown>;
        showCloseIcon?: boolean;
    }>(),
    {
        position: 'right',
        showCloseIcon: true,
        fallbackPartial: null,
        partialProps: () => ({}),
    }
);

const component = usePartialComponent(() => props.partial, {
    fallback: () => props.fallbackPartial,
});
const receiver = useDivisibleReceiver();

const isVertical = computed(
    () => props.position === 'left' || props.position === 'right'
);
const isHorizontal = computed(
    () => props.position === 'top' || props.position === 'bottom'
);
const isFull = computed(() => props.position === 'full');
const showCloseIcon = computed(() => props.showCloseIcon);

receiver.use('header');
const { component: TitlePart } = receiver.use('title');
const { component: SubTitlePart } = receiver.use('subtitle');
const { component: DescriptionPart, exists: descriptionExists } =
    receiver.use('description');
const { component: ToolsPart } = receiver.use('tools');
const { component: FooterPart, exists: footerExists } = receiver.use('footer');
const { component: ActionsPart } = receiver.use('actions');
</script>

<template>
    <Sidebar
        :position="position"
        class="sidebar-receiver"
        :class="{
            'sidebar-receiver-horizontal': isHorizontal,
            'sidebar-receiver-vertical': isVertical,
            'sidebar-receiver-full': isFull,
        }"
        :show-close-icon="showCloseIcon"
    >
        <template #header>
            <div
                class="overflow-hidden text-nowrap"
                :class="{ 'mr-auto': isVertical || !descriptionExists }"
            >
                <h5 class="mb-0 txt-ellipsis">
                    <TitlePart>
                        <template #skeleton>
                            <Skeleton
                                style="
                                    height: 2rem;
                                    width: 12rem;
                                    display: inline-block;
                                "
                            />
                        </template>
                    </TitlePart>
                </h5>
                <div class="text-400 font-medium txt-ellipsis">
                    <SubTitlePart>
                        <template #skeleton>
                            <Skeleton
                                style="
                                    height: 1.4rem;
                                    width: 10rem;
                                    display: inline-block;
                                "
                            />
                        </template>
                    </SubTitlePart>
                </div>
            </div>

            <div
                v-if="!isVertical && descriptionExists"
                class="mr-auto ml-2 p-2 border-gray-100 border-left-1 txt-ellipsis text-gray-400"
            >
                <DescriptionPart>
                    <template #skeleton>
                        <Skeleton style="width: 28rem" />
                    </template>
                </DescriptionPart>
            </div>

            <ToolsPart>
                <template #skeleton>
                    <Button
                        class="p-button-rounded p-button-text p-button-secondary"
                        icon="pi pi-spinner pi-spin"
                        disabled
                    />
                </template>
            </ToolsPart>
        </template>

        <div class="sidebar-receiver-content">
            <p
                v-if="isVertical && descriptionExists"
                class="mb-2 pb-2 border-gray-100 border-bottom-1 text-gray-400"
            >
                <DescriptionPart>
                    <template #skeleton>
                        <Skeleton style="width: 100%; display: inline-block" />
                        <Skeleton style="width: 40%; display: inline-block" />
                    </template>
                </DescriptionPart>
            </p>

            <component :is="component" v-bind="partialProps">
                <slot />
            </component>

            <Divider />
        </div>
        <div class="sidebar-receiver-footerwrapper">
            <div class="sidebar-receiver-actions">
                <div v-if="footerExists" class="sidebar-receiver-footer">
                    <FooterPart>
                        <template #skeleton>
                            <Skeleton
                                style="
                                    width: 7rem;
                                    max-width: 50%;
                                    margin-bottom: 0.25rem;
                                "
                            />
                            <Skeleton style="width: 14rem; max-width: 80%" />
                        </template>
                    </FooterPart>
                </div>
            </div>
        </div>
        <div class="flex justify-content-between ml-4 mr-4">
            <ActionsPart>
                <template #skeleton>
                    <Skeleton style="height: 2.2rem" />
                    <Skeleton style="height: 2.2rem" />
                </template>
            </ActionsPart>
            <div>
                <h4 class="m-0 gino">GINO</h4>
                <h6 class="m-0 text-300 font-medium">
                    Copyright &copy; Shrd BV.
                </h6>
            </div>
        </div>
    </Sidebar>
</template>

<style lang="scss">
.sidebar-receiver {
    .sidebar-receiver-content {
        overflow: auto;
        padding: 1rem;
    }

    .sidebar-receiver-actions {
        padding: 1rem;
        display: flex;
        flex-direction: column-reverse;
        row-gap: 0.5rem;
    }

    .sidebar-receiver-footer {
        border-top: 1px solid var(--gray-100);
        margin-top: auto;
        padding: 0.5rem;
        font-size: small;
        color: var(--gray-400);
    }

    &.sidebar-receiver-vertical {
        .p-sidebar-content {
            display: flex;
            flex-direction: column;
            height: 100%;
            justify-content: flex-end;
        }

        .sidebar-receiver-content {
            flex-grow: 1;
            flex-shrink: 1;
        }

        .sidebar-receiver-footerwrapper {
            flex-grow: 0;
            flex-shrink: 0;
        }

        .sidebar-receiver-actions {
            justify-content: start;
        }
    }

    &.sidebar-receiver-horizontal {
        height: 320px;
        max-height: fit-content;

        .p-sidebar-content {
            overflow-y: hidden;
            display: flex;
        }

        .sidebar-receiver-content {
            flex-shrink: 1;
            flex-grow: 1;
        }

        .sidebar-receiver-footerwrapper {
            overflow-y: auto;
            flex-shrink: 0;
            flex-grow: 0;
        }

        .sidebar-receiver-actions {
            & > * {
                flex-grow: 0;
                flex-shrink: 0;
            }
            min-width: 18rem;
            height: 100%;
        }
    }
}

.sidebar-receiver .p-sidebar-header-content {
    width: 100% !important;
    display: flex;
    justify-items: end;
    align-items: center;
}
</style>
